<template>
    <Portal to="sidepanel">
        <SidePanel
            key="quizSettingsSidePanel"
            :is-dark-mode="isDarkMode"
            name="Quiz Settings"
            class="quiz-settings"
            width="medium"
            @close="emitClose"
        >
            <label class="quiz-settings__label">
                These controls will be default settings for all of your quiz modes.
            </label>
            <QuizSetting
                v-model="showAnswersAtEnd"
                :options="showAnswersAtEndOptions"
                class="quiz-settings__setting"
            />
            <QuizSetting
                v-if="!showAnswersAtEnd"
                v-model="showCheckAnswerButton"
                :options="showCheckAnswerButtonOptions"
                class="quiz-settings__setting"
            />
            <label class="quiz-settings__label quiz-settings__subjects-label">
                Adjust Subjects
            </label>
            <div v-dark class="quiz-settings__subjects-sublabel">
                Turn off a subject to hide all questions for that subject. It’s helpful if your exam has optional
                subjects that you don’t need to study for.
            </div>
            <div class="quiz-settings__subjects">
                <SubjectsSelector
                    v-if="currentExamMetadata"
                    :exam-metadata="currentExamMetadata"
                    mode="settings"
                    @disabledSubjects="updateDisabledSubjects"
                />
            </div>
            <template #action>
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :is-loading="isLoading"
                    :disabled="!settingsUnsaved"
                    @click="saveSettings"
                >
                    Save Settings
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { difference } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import SubjectsSelector from '@/components/Study/SubjectsSelector.vue'
import QuizSetting from '@/components/Study/QuizSetting.vue'

const {
    SidePanel,
    Button: PocketButton,
} = UIKit

const emit = defineEmits<{
    close: []
}>()

const showAnswersAtEnd = ref(false)
const showCheckAnswerButton = ref(false)
const disabledSubjectIds = ref<string[]>([])
const isLoading = ref(true)
const showAnswersAtEndOptions = [
    {
        value: false,
        label: 'Show answers as I go',
    },
    {
        value: true,
        label: 'Show answers at the end',
    },
]
const showCheckAnswerButtonOptions = [
    {
        value: true,
        label: 'Manual Submit (Click "Check Answer" Button)',
    },
    {
        value: false,
        label: 'Automatic Submit (Click Answer)',
    },
]

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const settingsUnsaved = computed(() => {
    const userQuizSettings = userModule.state.user?.quizSettings
    const userDisabledSubjects = uemDisabledSubjects.value
    const disabledSubjects = disabledSubjectIds.value
    if (!userQuizSettings) {
        return true
    }
    return userQuizSettings.showCheckAnswerButton !== showCheckAnswerButton.value
        || userQuizSettings.showAnswersAtEnd !== showAnswersAtEnd.value
        || difference(disabledSubjects, userDisabledSubjects || []).length
        || difference(userDisabledSubjects || [], disabledSubjects).length
})

const subjects = computed(() => {
    return examMetadataModule.state.subjectsWithLevels
})

const uemDisabledSubjects = computed(() => {
    return userExamMetadataModule.getters.getUemDisabledSubjects(
        currentExamMetadata.value?.examGuid,
        subjects.value
    )
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    showAnswersAtEnd.value = userModule.state.user?.quizSettings?.showAnswersAtEnd || false
    showCheckAnswerButton.value = userModule.state.user?.quizSettings?.showCheckAnswerButton || false
    disabledSubjectIds.value = uemDisabledSubjects.value

    isLoading.value = false
})

const updateDisabledSubjects = (subjectIdsParam: string[]) => {
    disabledSubjectIds.value = subjectIdsParam

}

const saveSettings = async () => {
    isLoading.value = true
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    if (!uem) {
        throw new Error('No User Exam Metadata found.')
    }

    // Continue to update disabledSubjectNames until clients are fully switched over to subjectIds
    const disabledSubjectNames = subjects.value.reduce<string[]>((acc, subject) => {
        if (disabledSubjectIds.value.includes(subject.subjectId)) {
            acc.push(subject.subjectName)
        }
        return acc
    }, [])

    await Promise.all([
        userModule.actions.updateQuizSettings({
            showCheckAnswerButton: showCheckAnswerButton.value,
            showAnswersAtEnd: showAnswersAtEnd.value,
        }),
        userExamMetadataModule.actions.upsertUserExamMetadata({
            objectId: uem.objectId,
            disabledSubjects: disabledSubjectNames,
            disabledSubjectIds: disabledSubjectIds.value,
        }),
    ])
    emitClose()
    isLoading.value = false
}

const emitClose = () => {
    emit('close')
}
</script>
<style lang="scss" scoped>
.quiz-settings {
    &__label {
        font-size: 15px;
        line-height: 20px;
        font-weight: 600;
        margin-bottom: 17px;
        display: block;
    }

    &__subjects {
        width: calc(100% + 30px);
        margin-left: -15px;
        margin-bottom: 36px;
    }

    &__setting {
        width: calc(100% + 30px);
        margin-left: -15px;
        margin-bottom: 12px;
    }

    &__subjects-label {
        margin-top: 32px;
        margin-bottom: 10px;
    }

    &__subjects-sublabel {
        font-size: 14px;
        line-height: 18px;
        color: $slate-03;
        margin-bottom: 17px;

        &--dark {
            color: $pewter;
        }
    }
}
</style>
